import { SUPPORT_EMAIL } from "../../helpers/supportEmail";
import { AssistantCitation } from "../../api/assistant";
import { PageContext } from "./pageContext";

export type TranscriptTurn = { role: "user" | "assistant"; content: string; citations?: AssistantCitation[] };

export type MailLabels = {
  question: string; // "Q"
  answer: string; // "AI"
  page: string;
  language: string;
  congregation: string;
  omitted: string; // "…(earlier messages omitted)…"
  subjectPrefix: string; // "Hourglass AI assistant"
};

export const MAX_MAILTO_LENGTH = 1800;
const SUBJECT_QUESTION_CHARS = 60;

function formatTurn(turn: TranscriptTurn, labels: MailLabels): string {
  const label = turn.role === "user" ? labels.question : labels.answer;
  let text = `${label}: ${turn.content}`;
  if (turn.role === "assistant" && turn.citations?.length) {
    text += "\n" + turn.citations.map((c) => `[${c.n}] ${c.title} ${c.link}`).join("\n");
  }
  return text;
}

function header(ctx: PageContext, congName: string, labels: MailLabels): string {
  return [
    "", // blank line at top for the user's note
    "",
    `${labels.page}: ${ctx.pathname}`,
    `${labels.language}: ${ctx.lang}`,
    `${labels.congregation}: ${congName}`,
    "",
  ].join("\n");
}

/** Full plain-text transcript (used by Copy conversation). Same layout as the mail body. */
export function buildFullTranscript(
  turns: TranscriptTurn[],
  ctx: PageContext,
  congName: string,
  labels: MailLabels,
): string {
  return header(ctx, congName, labels).trimStart() + "\n" + turns.map((t) => formatTurn(t, labels)).join("\n\n");
}

/** Cuts to at most `n` UTF-16 units without ending in half a surrogate pair (which encodeURIComponent rejects). */
function safeSlice(s: string, n: number): string {
  const out = s.slice(0, n);
  const last = out.charCodeAt(out.length - 1);
  return last >= 0xd800 && last <= 0xdbff ? out.slice(0, -1) : out;
}

function mailto(subject: string, body: string): string {
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/**
 * Builds the support mailto: link. Body order: blank note line, page, language/congregation, transcript.
 * If the encoded URL exceeds MAX_MAILTO_LENGTH, keeps the first turn and as many of the latest turns as fit,
 * with an omitted marker in between.
 */
export function buildSupportMailto(
  turns: TranscriptTurn[],
  ctx: PageContext,
  congName: string,
  labels: MailLabels,
  maxLength: number = MAX_MAILTO_LENGTH,
): string {
  const firstQuestion = turns.find((t) => t.role === "user")?.content ?? "";
  const subject = `${labels.subjectPrefix}: ${safeSlice(firstQuestion.replace(/\s+/g, " ").trim(), SUBJECT_QUESTION_CHARS)}`;
  const head = header(ctx, congName, labels);
  const build = (parts: string[]) => mailto(subject, head + "\n" + parts.join("\n\n"));

  const formatted = turns.map((t) => formatTurn(t, labels));
  let url = build(formatted);
  if (url.length <= maxLength || formatted.length === 0) return url;

  const first = formatted[0]!;
  const tail: string[] = [];
  for (let i = formatted.length - 1; i >= 1; i--) {
    const candidate = build([first, labels.omitted, formatted[i]!, ...tail]);
    if (candidate.length > maxLength) break;
    tail.unshift(formatted[i]!);
  }
  if (tail.length === formatted.length - 1) return build(formatted);
  url = build([first, labels.omitted, ...tail]);
  if (url.length <= maxLength) return url;
  // even first + marker is too long: truncate the first turn's text
  let text = first;
  while (text.length > 0 && build([text + "…", labels.omitted]).length > maxLength) {
    text = safeSlice(text, Math.max(0, text.length - 50));
  }
  return build([text + "…", labels.omitted]);
}
